import {
  PrimaryButton,
  PrimaryButtonProps,
  TextInput,
} from "@brexhq/component-library";
import { Text } from "@brexhq/metal-text";
import styled from "@emotion/styled";
import * as React from "react";

import { useFormSubmissionContext } from "@brexhq/next/src/components/modules/LeadForm/FormSubmissionProvider";
import { useLeadFormModuleContext } from "@brexhq/next/src/components/modules/LeadForm/LeadFormModuleProvider";
import { GTMInlineEmailSuccess } from "@brexhq/next/src/components/modules/LeadForm/Steps/GTMSteps/GTMInlineEmailSuccess";
import { GTMFormPageTemplate } from "@brexhq/next/src/components/modules/LeadForm/Steps/PageTemplates/GTMFormPageTemplate";
import { LeadFormSteps } from "@brexhq/next/src/components/modules/LeadForm/machines/types";
import { LeadFormValues } from "@brexhq/next/src/components/modules/LeadForm/sharedTypes";
import { DarkModeOverride } from "@brexhq/next/src/contexts/Theme";
import { isDarkTheme } from "@brexhq/shared/types/ModuleTheme";

import isPersonalEmail from "../../../../../utils/isPersonalEmail";
import { invalidWorkEmailErrorMessage } from "../../../../building-blocks/MarketoForm/utils";

const InputWrapper = styled.div<{ hideBorder?: boolean; width?: string }>(
  ({ theme, hideBorder }) => {
    return {
      width: "100%", // Default to full width
      div: {
        paddingRight: 0,
        border: hideBorder ? "none" : undefined,
        borderRadius: theme.radius.textInputRadius,
      },
    };
  },
);

const WrappedButton = styled(PrimaryButton)<PrimaryButtonProps>(
  ({ theme }) => ({
    paddingLeft: theme.spacing[16],
    paddingRight: theme.spacing[16],
    margin: theme.spacing[8],
  }),
);

const SpacingSpan = styled(Text)(({ theme }) => ({
  paddingLeft: theme.spacing[4],
  color: theme.colors.content.tertiary,
}));

export const GTMInlineEmail: React.FC<{
  isLoading: boolean;
  success: boolean;
  inlineEmailFieldWidth?: string;
  checkWorkEmail?: boolean;
}> = ({
  isLoading,
  success,
  inlineEmailFieldWidth,
  checkWorkEmail = false,
}) => {
  const {
    submit,
    form: { fields, clearErrors, getValues },
    setSubmitError,
    submitError,
  } = useFormSubmissionContext();

  const { customSubmitButtonLabel, customProgramName, theme } =
    useLeadFormModuleContext();

  const handleEmailChange = React.useCallback(
    (value: string) => {
      fields.workEmail.onChange({ value });
      clearErrors();
      setSubmitError(null);
    },
    [fields.workEmail, clearErrors, setSubmitError],
  );

  const onSubmit = React.useCallback(
    async (event) => {
      const fieldList: (keyof LeadFormValues)[] = ["workEmail"];
      const emailFieldValue = getValues("workEmail");

      if (
        checkWorkEmail &&
        emailFieldValue &&
        isPersonalEmail(emailFieldValue)
      ) {
        setSubmitError({
          title: "Invalid email",
          message: invalidWorkEmailErrorMessage,
        });
        return;
      }

      /**
       * Handles form submission including validations.
       * Reference FormSubmissionProvider.tsx for more context
       */
      submit({
        event,
        fields: fieldList,
        prevStep: LeadFormSteps.userInfo,
      });
    },
    [submit, checkWorkEmail, getValues, setSubmitError],
  );

  const isDarkMode = isDarkTheme(theme);

  return (
    <GTMFormPageTemplate
      step={LeadFormSteps.gtmInlineEmail}
      programName={customProgramName}
      inlineForm
      inlineEmailFieldWidth={inlineEmailFieldWidth}
    >
      {success && <GTMInlineEmailSuccess />}
      {!success && (
        <DarkModeOverride useDarkMode={false}>
          <InputWrapper
            hideBorder={isDarkMode && !fields.workEmail.error}
            width={inlineEmailFieldWidth}
          >
            <TextInput
              inputMode="email"
              autoComplete="email"
              name={fields.workEmail.name}
              aria-label={fields.workEmail.label}
              onChange={(e) => handleEmailChange(e.value)}
              value={fields.workEmail.value}
              placeholder="Enter your work email"
              error={
                fields.workEmail.error ? (
                  <DarkModeOverride useDarkMode={isDarkMode}>
                    <SpacingSpan>{fields.workEmail.error}</SpacingSpan>
                  </DarkModeOverride>
                ) : submitError ? (
                  <DarkModeOverride useDarkMode={isDarkMode}>
                    <SpacingSpan>{submitError.message}</SpacingSpan>
                  </DarkModeOverride>
                ) : null
              }
              redactionStrategy="lipsum"
              afterInput={
                <WrappedButton onClick={onSubmit} loading={isLoading}>
                  {customSubmitButtonLabel ?? "Get started"}
                </WrappedButton>
              }
            />
          </InputWrapper>
        </DarkModeOverride>
      )}
    </GTMFormPageTemplate>
  );
};
